import { useState, useEffect } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  RadioGroup,
  Radio,
  Stack,
  HStack,
  VStack,
  useToast,
  Box,
  Divider,
  IconButton,
  Grid,
  GridItem,
  Text,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Progress,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { Plus, X, Target, Calendar, User as UserIcon, Briefcase, AlignLeft, Layers } from 'lucide-react';
import { Rock, User, Department } from '../../types';
import { formatDateForInput } from '../../../../utils/date.utils';
import { useModalStyles } from '../../../../utils/modal.styles';

interface RockFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any) => Promise<void>;
  editingRock: Rock | null;
  currentUserId?: string;
  users: User[];
  departments: Department[];
  getMilestones?: (rockId: string) => Promise<any[]>;
}

/**
 * Rock Form Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI
 */
export function RockFormModal({
  isOpen,
  onClose,
  onSave,
  editingRock,
  currentUserId,
  users,
  departments,
  getMilestones,
}: RockFormModalProps) {
  const toast = useToast();
  const styles = useModalStyles();
  const [milestoneStats, setMilestoneStats] = useState<{ total: number; completed: number }>({ total: 0, completed: 0 });

  // Load milestones when editing a rock to get the count and completion status
  useEffect(() => {
    if (editingRock && getMilestones) {
      const fetchMilestones = async () => {
        try {
          const milestones = await getMilestones(editingRock.id);
          const total = milestones.length;
          const completed = milestones.filter((m: any) => m.complete).length;
          setMilestoneStats({ total, completed });
        } catch (error) {
          console.error('Error loading milestones for form:', error);
          setMilestoneStats({ total: 0, completed: 0 });
        }
      };
      fetchMilestones();
    } else {
      setMilestoneStats({ total: 0, completed: 0 });
    }
  }, [editingRock, getMilestones]);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    rock: Yup.string()
      .required('Rock title is required')
      .min(3, 'Rock title must be at least 3 characters'),
    measurable: Yup.string()
      .required('Measurable is required')
      .min(3, 'Measurable must be at least 3 characters'),
    dueDate: Yup.date()
      .required('Due date is required')
      .min(new Date(), 'Due date must be in the future'),
    ownerId: Yup.string().required('Owner is required'),
    // status: Yup.string().oneOf(['on-track', 'off-track']), // Removed status field
    // progress: Yup.number().min(0).max(100), // Progress is now read-only derived from milestones
    rockType: Yup.string().oneOf(['individual', 'company']),
    milestones: editingRock ? Yup.array() : Yup.array()
      .of(
        Yup.object({
          task: Yup.string().required('Task is required').min(3, 'Task must be at least 3 characters'),
          dueDate: Yup.date().required('Due date is required'),
        })
      )
      .min(3, 'At least 3 milestones are required to create a rock'), // Requirement: 3 milestones min
    actionItems: Yup.array().of(
      Yup.object({
        task: Yup.string().required('Task is required').min(3, 'Task must be at least 3 characters'),
        assigneeId: Yup.string().nullable(),
        dueDate: Yup.date().required('Due date is required'),
      })
    ),
  });

  const initialValues = {
    rock: editingRock?.rock || '',
    measurable: editingRock?.measurable || '',
    dueDate: editingRock?.dueDate
      ? formatDateForInput(editingRock.dueDate)
      : '',
    ownerId: editingRock?.ownerId || currentUserId || '',
    // status: editingRock?.status || 'on-track', // Status removed from edit form
    progress: editingRock?.progress || 0,
    notes: editingRock?.notes || '',
    collaborators: editingRock?.collaborators || [],
    rockType: editingRock?.rockType || 'individual',
    departmentId: editingRock?.departmentId || '',
    milestones: [] as Array<{ task: string; dueDate: string }>,
    actionItems: [] as Array<{ task: string; assigneeId?: string; dueDate: string }>,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await onSave({
        ...values,
        dueDate: values.dueDate,
        // Convert empty string to undefined for departmentId
        departmentId: values.departmentId || undefined,
        // Progress is not manually editable here for new rocks, 
        // but for editing it might be preserved or recalculated
      });
      toast({
        title: editingRock ? 'Rock updated' : 'Rock created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save rock',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg={styles.modal.overlayBg} />
      <ModalContent bg={styles.modal.bg}>
        <ModalHeader bg={styles.header.bg} borderTopRadius="md">
          {editingRock ? 'Edit Rock' : 'Add New Rock'}
        </ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, values, setFieldValue, errors, touched }) => (
            <Form>
              <ModalBody pb={6}>
                <VStack spacing={6} align="stretch">
                  {/* Progress Bar - Top of Modal (Jazzed up) */}
                  {editingRock && (
                    <Box 
                      p={4} 
                      borderRadius="xl" 
                      bgGradient={styles.progressCard.bgGradient}
                      borderWidth="1px" 
                      borderColor={styles.progressCard.borderColor}
                      boxShadow="sm"
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="bold" fontSize="sm" color={styles.text.label} display="flex" alignItems="center">
                          <Icon as={Target} mr={2} color={styles.icon.active} />
                          Rock Completion
                        </Text>
                        <Badge 
                          colorScheme={
                            milestoneStats.total > 0 
                              ? (milestoneStats.completed / milestoneStats.total) >= 1 ? 'green' : 'blue'
                              : 'gray'
                          }
                          variant="solid"
                          borderRadius="full"
                          px={2}
                        >
                          {milestoneStats.total > 0 
                            ? Math.round((milestoneStats.completed / milestoneStats.total) * 100) 
                            : 0}%
                        </Badge>
                      </HStack>
                      <Box position="relative" h="12px" w="100%" borderRadius="full" bg={styles.progressCard.trackBg}>
                        <Box
                          position="absolute"
                          left="0"
                          top="0"
                          h="100%"
                          w={`${milestoneStats.total > 0 ? (milestoneStats.completed / milestoneStats.total) * 100 : 0}%`}
                          borderRadius="full"
                          bgGradient={styles.progressCard.filledBg}
                          transition="width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                          boxShadow="0 0 10px rgba(66, 153, 225, 0.6)"
                        />
                      </Box>
                      <Text fontSize="xs" color={styles.text.muted} mt={2} textAlign="right">
                        {milestoneStats.completed} / {milestoneStats.total} milestones completed
                      </Text>
                    </Box>
                  )}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Field name="rock">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched} isRequired>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Rock Title</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={Target} color={styles.icon.default} />} />
                              <Input 
                                {...field} 
                                placeholder="Enter rock title" 
                                size="lg" 
                                bg={styles.input.bg}
                                borderColor={styles.input.borderColor}
                                _placeholder={{ color: styles.input.placeholderColor }}
                                _focus={styles.input._focus}
                              />
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>

                    <GridItem>
                      <Field name="rockType">
                        {({ field }: any) => (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Rock Type</FormLabel>
                            <Box 
                              p={2} 
                              borderWidth="1px" 
                              borderColor={styles.input.borderColor}
                              borderRadius="md" 
                              bg={styles.input.bg}
                            >
                              <RadioGroup
                                {...field}
                                value={values.rockType}
                                onChange={(value) => setFieldValue('rockType', value)}
                              >
                                <Stack direction="row" spacing={4}>
                                  <Radio value="individual">Individual</Radio>
                                  <Radio value="company">Company</Radio>
                                </Stack>
                              </RadioGroup>
                            </Box>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>

                    <GridItem>
                      <Field name="departmentId">
                        {({ field }: any) => (
                          <FormControl>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Department (Optional)</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={Briefcase} color={styles.icon.default} />} />
                              <Select {...field} placeholder="Select Department" pl={10} bg={styles.input.bg} borderColor={styles.input.borderColor}>
                                <option value="">No Department</option>
                                {departments.map((dept) => (
                                  <option key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </Select>
                            </InputGroup>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>

                    <GridItem>
                      <Field name="ownerId">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched} isRequired>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Owner</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={UserIcon} color={styles.icon.default} />} />
                              <Select {...field} placeholder="Select Owner" pl={10} bg={styles.input.bg} borderColor={styles.input.borderColor}>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </Select>
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>

                    <GridItem>
                      <Field name="dueDate">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched} isRequired>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Due Date</FormLabel>
                            <InputGroup>
                              <InputLeftElement pointerEvents="none" children={<Icon as={Calendar} color={styles.icon.default} />} />
                              <Input {...field} type="date" pl={10} bg={styles.input.bg} borderColor={styles.input.borderColor} />
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Field name="measurable">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched} isRequired>
                            <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Measurable</FormLabel>
                            <InputGroup>
                               <InputLeftElement pointerEvents="none" children={<Icon as={AlignLeft} color={styles.icon.default} />} />
                              <Input
                                {...field}
                                placeholder="How will success be measured?"
                                pl={10}
                                bg={styles.input.bg}
                                borderColor={styles.input.borderColor}
                                _placeholder={{ color: styles.input.placeholderColor }}
                              />
                            </InputGroup>
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </GridItem>
                  </SimpleGrid>

                  <Field name="notes">
                    {({ field }: any) => (
                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="bold" color={styles.text.label}>Notes</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Additional notes..."
                          rows={3}
                          bg={styles.input.bg}
                          borderColor={styles.input.borderColor}
                          _placeholder={{ color: styles.input.placeholderColor }}
                          _focus={styles.input._focus}
                        />
                      </FormControl>
                    )}
                  </Field>

                  {/* Milestones and Action Items - Only show when creating a new rock */}
                  {!editingRock && (
                    <>
                      <Divider my={2} borderColor={styles.section.dividerColor} />
                      <Box>
                        <HStack mb={4}>
                           <Icon as={Layers} color={styles.icon.active} />
                           <Text fontSize="md" fontWeight="bold" color={styles.text.primary}>
                            Milestones
                           </Text>
                           <Badge colorScheme="red" variant="subtle">Required: 3+</Badge>
                        </HStack>
                        
                        <FieldArray name="milestones">
                          {({ push, remove, form }: any) => (
                            <VStack spacing={3} align="stretch" bg={styles.section.bg} p={4} borderRadius="md" borderWidth="1px" borderColor={styles.section.borderColor}>
                              {form.values.milestones.map((milestone: any, index: number) => (
                                <HStack key={index} spacing={2} align="flex-start">
                                  <Field name={`milestones.${index}.task`}>
                                    {({ field, meta }: any) => (
                                      <FormControl
                                        isInvalid={meta.error && meta.touched}
                                        flex={2}
                                      >
                                        <Input
                                          {...field}
                                          placeholder={`Milestone ${index + 1}`}
                                          size="sm"
                                          bg={styles.modal.bg}
                                          borderColor={styles.input.borderColor}
                                        />
                                        <FormErrorMessage fontSize="xs">{meta.error}</FormErrorMessage>
                                      </FormControl>
                                    )}
                                  </Field>
                                  <Field name={`milestones.${index}.dueDate`}>
                                    {({ field, meta }: any) => (
                                      <FormControl
                                        isInvalid={meta.error && meta.touched}
                                        flex={1}
                                      >
                                        <Input {...field} type="date" size="sm" bg={styles.modal.bg} borderColor={styles.input.borderColor} />
                                        <FormErrorMessage fontSize="xs">{meta.error}</FormErrorMessage>
                                      </FormControl>
                                    )}
                                  </Field>
                                  <IconButton
                                    aria-label="Remove milestone"
                                    icon={<Icon as={X} w={4} h={4} />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => remove(index)}
                                    mt={0.5}
                                  />
                                </HStack>
                              ))}
                              <Button
                                size="sm"
                                leftIcon={<Icon as={Plus} w={4} h={4} />}
                                onClick={() => push({ task: '', dueDate: '' })}
                                variant="outline"
                                colorScheme="blue"
                                w="full"
                              >
                                Add Milestone
                              </Button>
                              {typeof errors.milestones === 'string' && touched.milestones && (
                                <Text color="red.500" fontSize="sm" textAlign="center">
                                  {errors.milestones}
                                </Text>
                              )}
                            </VStack>
                          )}
                        </FieldArray>
                      </Box>

                      <Box>
                        <Text fontSize="md" fontWeight="bold" color={styles.text.primary} mb={4}>
                          Action Items <Text as="span" fontSize="sm" fontWeight="normal" color={styles.text.muted}>(Optional)</Text>
                        </Text>
                        <FieldArray name="actionItems">
                          {({ push, remove, form }: any) => (
                            <VStack spacing={3} align="stretch" bg={styles.section.bg} p={4} borderRadius="md" borderWidth="1px" borderColor={styles.section.borderColor}>
                              {form.values.actionItems.map((actionItem: any, index: number) => (
                                <HStack key={index} spacing={2} align="flex-start">
                                  <Field name={`actionItems.${index}.task`}>
                                    {({ field, meta }: any) => (
                                      <FormControl
                                        isInvalid={meta.error && meta.touched}
                                        flex={2}
                                      >
                                        <Input
                                          {...field}
                                          placeholder="Action item task"
                                          size="sm"
                                          bg={styles.modal.bg}
                                          borderColor={styles.input.borderColor}
                                        />
                                        <FormErrorMessage fontSize="xs">{meta.error}</FormErrorMessage>
                                      </FormControl>
                                    )}
                                  </Field>
                                    <Field name={`actionItems.${index}.assigneeId`}>
                                    {({ field }: any) => (
                                      <FormControl flex={1}>
                                        <Select {...field} placeholder="Assign to" size="sm" bg={styles.modal.bg} borderColor={styles.input.borderColor}>
                                          {users.map((user) => (
                                            <option key={user.id} value={user.id}>
                                              {user.name}
                                            </option>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </Field>
                                  <Field name={`actionItems.${index}.dueDate`}>
                                    {({ field, meta }: any) => (
                                      <FormControl
                                        isInvalid={meta.error && meta.touched}
                                        flex={1}
                                      >
                                        <Input {...field} type="date" size="sm" bg={styles.modal.bg} borderColor={styles.input.borderColor} />
                                        <FormErrorMessage fontSize="xs">{meta.error}</FormErrorMessage>
                                      </FormControl>
                                    )}
                                  </Field>
                                  <IconButton
                                    aria-label="Remove action item"
                                    icon={<Icon as={X} w={4} h={4} />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => remove(index)}
                                    mt={0.5}
                                  />
                                </HStack>
                              ))}
                              <Button
                                size="sm"
                                leftIcon={<Icon as={Plus} w={4} h={4} />}
                                onClick={() => push({ task: '', assigneeId: '', dueDate: '' })}
                                variant="outline"
                                w="full"
                              >
                                Add Action Item
                              </Button>
                            </VStack>
                          )}
                        </FieldArray>
                      </Box>
                    </>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter bg={styles.footer.bg} borderTopWidth="1px" borderColor={styles.footer.borderColor}>
                <Button variant="ghost" mr={3} onClick={onClose} size="lg" _hover={styles.button.ghost._hover}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                  size="lg"
                  px={8}
                  bgGradient={styles.button.primary.bgGradient}
                  _hover={{ bgGradient: styles.button.primary._hover.bgGradient, boxShadow: 'md' }}
                >
                  {editingRock ? 'Update Rock' : 'Create Rock'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
